import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { DndContext, DragOverlay, closestCenter, pointerWithin, PointerSensor, TouchSensor, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, horizontalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { POS_COLORS } from '../data/players';
import {
  buildPosTierData,
  lsKey, LS_MOVES, LS_ORDER, LS_NAMES,
  loadOverrides, loadTierOrder, loadTierNames,
} from '../data/displayTiers';
import { getEffectivePosRank, getAdpValue, parseRankNum } from '../data/adpData';
import { computeDraftedCounts, computePrintDepth } from '../hooks/usePricing';
import { usePlayerCard } from '../context/PlayerCardContext';
import { captureTierPlayerMoved, captureTierCheatSheet } from '../utils/projlabAnalytics';
import { LeagueContextLine } from './LeagueChip';
import StatsButton, { StatsHintStrip } from './StatsButton';
import { draftValueKey, draftValuesStorageKey } from '../utils/draftValuesBridge';

const POSITIONS = ["QB", "RB", "WR", "TE"];

// Print depth. A row prints if its position rank is inside the depth computed
// by computePrintDepth (usePricing.js — the multipliers live next to the counts
// they scale), OR the user values it at $2+ regardless of rank. Depth therefore
// scales with league size instead of a fixed dollar line. Screen rendering is
// untouched by all of this.
const PRINT_MIN_PRICE = 2;

// One-time read of the explicit "Build Draft Values" snapshot from projlab, for
// the ACTIVE league (per-league key). Same read PlayerList.jsx does. Returns the
// full snapshot or null when none exists (→ the cheat sheet renders exactly as
// it did before values existed).
function readMyDraftValues(league) {
  try {
    return JSON.parse(localStorage.getItem(draftValuesStorageKey(league))) || null;
  } catch { return null; }
}

// Optional per-league record of what this room actually paid last year, by
// position rank slot: { QB: [50,45,...], RB: [...], ... }, each array
// desc-sorted real prices. Seeded by hand today; same shape a future "import
// past auction" feature would write. No league data ships in the codebase —
// absent key → the blue LY column never renders.
function readLyPrices(league) {
  try {
    return JSON.parse(localStorage.getItem(`auctionArchitect_lyPrices_${league || "DEFAULT"}`)) || null;
  } catch { return null; }
}

// ── Cheat-sheet draft marks ─────────────────────────────────────────────────
// Purely local display state for tracking a live draft on the printed/on-screen
// sheet: keyed `${name}-${pos}` (same composite as the price lookup), valued
// "gone" | "mine". Per-league, so marking up the Summit sheet can't touch any
// other league. Deliberately NOT wired to watchlist/avoids or anything the
// engine reads — this is a pen on paper, nothing more.
const cheatMarksKey = (league) => `auctionArchitect_cheatMarks_${league || "DEFAULT"}`;

function readCheatMarks(league) {
  try {
    return JSON.parse(localStorage.getItem(cheatMarksKey(league))) || {};
  } catch { return {}; }
}

// ── Watchlist / avoid badges (READ-ONLY) ────────────────────────────────────
// The Tier Builder shows which players are starred or on the avoid list, but it
// never toggles them: the pill is a drag handle, so any tap target on it would
// fight dnd-kit. Toggling lives in PlayerList / TierViewer.
//
// Delivered by a file-local context rather than prop-drilled: PlayerPill sits
// under SortableCard, the tier lanes, the DragOverlay and the cheat sheet, and
// threading two read-only Sets through all of them would touch drag internals
// for no reason. Both Sets are keyed by BARE PLAYER NAME — same as the
// membership checks in PlayerList (`watchlist.has(player.name)`), NOT the
// `name-pos` composite that dynamicPrices uses.
const EMPTY_BADGES = { watchlist: new Set(), avoids: new Set() };
const TierBadgeContext = React.createContext(EMPTY_BADGES);

// Renders nothing at all when the player is in neither set, so unbadged pills
// are byte-identical to before. pointerEvents:none — never intercepts a drag.
function PlayerBadges({ name, style }) {
  const { watchlist, avoids } = React.useContext(TierBadgeContext);
  const starred = watchlist?.has(name);
  const avoided = avoids?.has(name);
  if (!starred && !avoided) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2, pointerEvents: "none", lineHeight: 1, ...style }}>
      {starred && <span style={{ fontSize: 10, color: "#eab308", lineHeight: 1 }}>{"★"}</span>}
      {avoided && <span style={{ fontSize: 9, lineHeight: 1 }}>{"🚫"}</span>}
    </span>
  );
}

// Display-tier LS readers, key helpers, and buildPosTierData now live in
// ../data/displayTiers (shared with the in-draft TierViewer). The savers below
// stay here — only the Tier Builder writes these keys.

const TIER_COLORS = [
  "#22c55e", // T1
  "#3b82f6", // T2
  "#8b5cf6", // T3
  "#f59e0b", // T4
  "#ef4444", // T5
  "#ec4899", // T6
  "#14b8a6", // T7
  "#f97316", // T8
  "#6366f1", // T9
  "#a855f7", // T10
  "#78716c", // T11
];

function shortName(name) {
  const parts = name.split(' ');
  if (parts.length < 2) return name;
  return parts[0][0] + '. ' + parts.slice(1).join(' ');
}


function getTierColor(tier) {
  return TIER_COLORS[Math.min(tier - 1, TIER_COLORS.length - 1)] || "#64748b";
}

// Lane droppable ids are `tier-<n>`; player pill ids are the bare player name.
// This lets an EMPTY tier (no pills to collide with) still be a valid drop target.
function parseLaneId(id) {
  return (typeof id === "string" && id.startsWith("tier-")) ? Number(id.slice(5)) : null;
}

// Hybrid collision: droppables under the pointer win (a pill under the pointer
// still beats its lane; an empty lane is hittable edge-to-edge — closestCenter
// alone let an adjacent tier's pill win near a wide empty lane's left edge).
const collideHybrid = (args) => {
  const within = pointerWithin(args);
  return within.length ? within : closestCenter(args);
};

function saveOverrides(overrides, leagueName) {
  try {
    const pairs = Object.entries(overrides);
    localStorage.setItem(lsKey(LS_MOVES, leagueName), JSON.stringify(pairs));
  } catch { /* ignore */ }
}

function saveTierOrder(order, leagueName) {
  try {
    localStorage.setItem(lsKey(LS_ORDER, leagueName), JSON.stringify(order));
  } catch { /* ignore */ }
}

function saveTierNames(names, leagueName) {
  try {
    localStorage.setItem(lsKey(LS_NAMES, leagueName), JSON.stringify(names));
  } catch { /* ignore */ }
}

// --- Sortable player pill (desktop: entire pill is drag handle) ---
function SortableCard({ id, player, posColor, isOverridden }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "transform 0.2s ease",
    opacity: isDragging ? 0.35 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <PlayerPill player={player} posColor={posColor} isOverridden={isOverridden} isDragging={isDragging} />
    </div>
  );
}

// --- Player avatar with Sleeper headshot + fallback initials ---
function PlayerAvatar({ player, size = 28 }) {
  const [imgError, setImgError] = useState(false);
  const sid = player.sleeper_id;
  const isDef = player.pos === "DEF";
  const bgColor = POS_COLORS[player.pos]?.bg || "#64748b";

  if (sid && !isDef && !imgError) {
    return (
      <img
        src={`https://sleepercdn.com/content/nfl/players/${sid}.jpg`}
        alt=""
        onError={() => setImgError(true)}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: "#0d1320" }}
      />
    );
  }

  // Fallback: initials or team abbrev for DEF
  const parts = player.name.split(" ");
  const label = isDef
    ? (player.team || "DEF")
    : (parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : player.name.slice(0, 2)).toUpperCase();

  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: bgColor, display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: isDef ? 9 : 11, fontWeight: 700, color: "#fff", lineHeight: 1,
    }}>
      {label}
    </div>
  );
}

// --- Static pill (used in overlay + regular render + mobile) ---
function PlayerPill({ player, posColor, ghost, isOverridden, showGrip, isDragging, selected }) {
  const { dynamicPrices } = usePlayerCard();
  const adp = getAdpValue(player.name);
  // Static ADP/consensus position rank (the same rank the tiers seed from). It is
  // the outside market anchor — it does NOT change when the user moves a player
  // between tiers (derived from the player, not their tier placement).
  const posRank = getEffectivePosRank(player.name, player.posRank);
  // Canonical league-dynamic market price (same value as Team Builder/PlayerCard,
  // keyed `${name}-${pos}`). Also static across tier moves — moves are decoupled.
  const price = dynamicPrices?.get(`${player.name}-${player.pos}`) ?? 1;
  return (
    <div className="tier-pill" style={{
      // relative only so the badge cluster can be taken out of flow — it does
      // not affect the pill's own size or position.
      position: "relative",
      display: "flex",
      alignItems: "center",
      gap: 6,
      background: selected ? "#1e2d3d" : "#1a2332",
      borderRadius: 8,
      padding: "6px 10px",
      border: selected ? "1px solid #FF6B00" : ghost ? "1px solid #FF6B00" : "1px solid rgba(255,255,255,0.06)",
      borderLeft: selected ? "2px solid #FF6B00" : ghost ? "2px solid #FF6B00" : isOverridden ? "2px solid #FF6B00" : "1px solid rgba(255,255,255,0.06)",
      cursor: ghost ? "grabbing" : isDragging ? "grabbing" : showGrip ? "pointer" : "grab",
      boxShadow: selected ? "0 0 0 2px #FF9500, 0 0 14px rgba(255,107,0,0.45)" : ghost ? "0 8px 24px rgba(0,0,0,0.5)" : "none",
      flexShrink: 0,
      transition: "opacity 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
      WebkitUserSelect: "none",
      userSelect: "none",
      WebkitTouchCallout: "none",
      touchAction: "none",
    }}>
      {/* Star / avoid badges — absolutely positioned over the pill's top-left
          corner so they add no width or height and cannot reflow the lane. */}
      <PlayerBadges name={player.name} style={{
        position: "absolute", top: 1, left: 3, zIndex: 2,
        background: "rgba(13,19,32,0.85)", borderRadius: 4, padding: "1px 3px",
      }} />

      {/* Grip icon: mobile-only visual hint */}
      {showGrip && <span className="tier-grip" style={{
        color: "#475569", fontSize: 12, lineHeight: 1, flexShrink: 0, userSelect: "none",
        padding: "4px 2px", touchAction: "none",
      }}>{"\u2807"}</span>}
      <PlayerAvatar player={player} size={28} />
      <div style={{ minWidth: 0, overflow: "hidden", flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={player.name}>
          {shortName(player.name)}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 1 }}>
          <span style={{ fontSize: 10, color: "#FF9500", fontWeight: 700 }}>${price}</span>
          <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>{player.team}</span>
          {posRank && <span style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>{posRank}</span>}
          {adp && <span className="tier-adp" style={{ fontSize: 10, color: "#94a3b8" }}>ADP {adp}</span>}
        </div>
      </div>
      {!ghost && <StatsButton name={player.name} source="tier_builder" stopPointerDown />}
    </div>
  );
}

// --- Scroll shadow wrapper for desktop cards area ---
function ScrollShadowCards({ children }) {
  const scrollRef = useRef(null);
  const [showShadow, setShowShadow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      setShowShadow(el.scrollWidth > el.clientWidth + 2 && el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };
    check();
    el.addEventListener("scroll", check, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(check) : null;
    if (ro) ro.observe(el);
    return () => {
      el.removeEventListener("scroll", check);
      if (ro) ro.disconnect();
    };
  }, [children]);

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <div ref={scrollRef} className="tier-lane-cards" style={{
        display: "flex",
        gap: 6,
        padding: "8px 10px",
        overflowX: "auto",
        overflowY: "hidden",
        alignItems: "center",
        height: "100%",
      }}>
        {children}
      </div>
      {showShadow && (
        <div style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: 32,
          background: "linear-gradient(to right, transparent, rgba(15,23,42,0.9))",
          pointerEvents: "none",
          borderRadius: "0 8px 8px 0",
        }} />
      )}
    </div>
  );
}

// --- Shared lane label with inline editable name ---
function TierLaneLabel({ tier, tierNum, count, activePos, customName, onRename }) {
  const color = getTierColor(tierNum);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);
  const displayName = customName || tier.label;

  const startEdit = (e) => {
    e.stopPropagation();
    setDraft(displayName);
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (!trimmed || trimmed === tier.label) {
      // Clear custom name (revert to default)
      if (onRename) onRename(null);
    } else if (trimmed !== displayName) {
      if (onRename) onRename(trimmed);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commit(); }
    if (e.key === "Escape") { setEditing(false); }
  };

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  return (
    <div className="tier-lane-label" style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: "8px 12px",
      borderRight: `3px solid ${color}`,
      background: "rgba(255,255,255,0.02)",
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{
          background: color,
          color: "#fff",
          fontSize: 9,
          fontWeight: 800,
          padding: "2px 6px",
          borderRadius: 3,
          letterSpacing: "0.5px",
        }}>T{tierNum}</span>
        {editing ? (
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={handleKey}
            style={{
              background: "transparent",
              border: "none",
              borderBottom: "1px dashed #475569",
              outline: "none",
              fontSize: 11,
              fontWeight: 600,
              color: "#94a3b8",
              fontFamily: "inherit",
              padding: "0 2px",
              maxWidth: 120,
              width: 120,
            }}
          />
        ) : (
          <span
            onClick={onRename ? startEdit : undefined}
            style={{
              fontSize: 11, fontWeight: 600,
              color: customName ? "#c8d2e0" : "#94a3b8",
              cursor: onRename ? "pointer" : "default",
              borderBottom: onRename ? "1px dashed transparent" : "none",
              transition: "border-color 0.15s ease",
            }}
            onMouseEnter={(e) => { if (onRename) e.currentTarget.style.borderBottomColor = "#475569"; }}
            onMouseLeave={(e) => { if (onRename) e.currentTarget.style.borderBottomColor = "transparent"; }}
            title={onRename ? "Click to rename" : undefined}
          >
            {displayName}
          </span>
        )}
        <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>({count})</span>
        {/* Mobile scroll grip — safe scroll zone indicator */}
        <span className="tier-label-grip" style={{
          color: "#475569", fontSize: 10, lineHeight: 1, marginLeft: 4, letterSpacing: "1px",
        }}>{"\u2261"}</span>
      </div>
    </div>
  );
}

// --- Desktop droppable swim lane ---
function TierLane({ tier, tierNum, playerIds, playersMap, posColor, activePos, overrideKeys, pulse, customName, onRename }) {
  const isEmpty = playerIds.length === 0;
  // Register the whole lane as a droppable so empty tiers (and the blank space to
  // the right of pills) accept drops. Pills remain their own sortable droppables.
  const { setNodeRef: setLaneNodeRef } = useDroppable({ id: `tier-${tierNum}` });
  return (
    <div ref={setLaneNodeRef} className={`tier-lane${pulse ? " tier-lane-pulse" : ""}`} style={{
      background: "#0f172a",
      borderRadius: 8,
      border: isEmpty ? "1px dashed rgba(255,255,255,0.1)" : "1px solid rgba(255,255,255,0.06)",
      minHeight: 64,
      overflow: "hidden",
    }}>
      <TierLaneLabel tier={tier} tierNum={tierNum} count={playerIds.length} activePos={activePos} customName={customName} onRename={onRename} />
      <SortableContext items={playerIds} strategy={horizontalListSortingStrategy}>
        <ScrollShadowCards>
          {playerIds.map(id => {
            const p = playersMap[id];
            return p ? <SortableCard key={id} id={id} player={p} posColor={posColor} isOverridden={overrideKeys.has(`${activePos}:${id}`)} /> : null;
          })}
          {isEmpty && (
            <span style={{ fontSize: 11, color: "#334155", fontStyle: "italic", padding: "0 8px" }}>Drop players here</span>
          )}
        </ScrollShadowCards>
      </SortableContext>
    </div>
  );
}

// --- Mobile reorder arrow button ---
function ReorderArrow({ direction, disabled, onClick }) {
  return (
    <button
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
      style={{
        width: 28,
        height: 28,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 6,
        color: disabled ? "#475569" : "#94a3b8",
        fontSize: 12,
        fontFamily: "inherit",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.3 : 1,
        padding: 0,
        flexShrink: 0,
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
        transition: "opacity 0.15s ease",
      }}
    >
      {direction === "up" ? "\u25B2" : "\u25BC"}
    </button>
  );
}

// --- Mobile swim lane with tap-to-move ---
function MobileTierLane({ tier, tierNum, playerIds, playersMap, posColor, activePos, overrideKeys, pulse, selectedPlayer, onSelectPlayer, onMoveToTier, onReorder, customName, onRename }) {
  const isEmpty = playerIds.length === 0;
  const isTargetLane = selectedPlayer && selectedPlayer.currentTier !== tierNum;
  return (
    <div className={`tier-lane${pulse ? " tier-lane-pulse" : ""}`} style={{
      background: "#0f172a",
      borderRadius: 8,
      border: isTargetLane ? "1px solid rgba(255,107,0,0.4)" : isEmpty ? "1px dashed rgba(255,255,255,0.1)" : "1px solid rgba(255,255,255,0.06)",
      minHeight: 64,
      overflow: "hidden",
      transition: "border-color 0.2s ease",
    }}>
      <TierLaneLabel tier={tier} tierNum={tierNum} count={playerIds.length} activePos={activePos} customName={customName} onRename={onRename} />
      <div className="tier-lane-cards" style={{
        display: "flex",
        gap: 6,
        padding: "8px 10px",
        overflowX: "auto",
        overflowY: "hidden",
        alignItems: "center",
        height: "100%",
        flexWrap: "wrap",
      }}>
        {playerIds.map((id, idx) => {
          const p = playersMap[id];
          if (!p) return null;
          const isSelected = selectedPlayer?.name === id;
          return (
            <div
              key={id}
              style={{ display: "flex", alignItems: "center", gap: 4, WebkitTapHighlightColor: "transparent" }}
            >
              <div onClick={() => onSelectPlayer(isSelected ? null : { name: id, pos: activePos, currentTier: tierNum })}>
                <PlayerPill
                  player={p}
                  posColor={posColor}
                  isOverridden={overrideKeys.has(`${activePos}:${id}`)}
                  selected={isSelected}
                />
              </div>
              {isSelected && (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <ReorderArrow direction="up" disabled={idx === 0} onClick={() => onReorder(id, tierNum, -1)} />
                    <ReorderArrow direction="down" disabled={idx === playerIds.length - 1} onClick={() => onReorder(id, tierNum, 1)} />
                  </div>
                  {/* Lock affordance — confirm placement + exit move-mode */}
                  <button
                    onClick={(e) => { e.stopPropagation(); onSelectPlayer(null); }}
                    title="Done — lock placement"
                    style={{
                      minWidth: 40, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                      background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.5)", borderRadius: 8,
                      color: "#22c55e", fontSize: 13, fontWeight: 800, fontFamily: "inherit",
                      cursor: "pointer", flexShrink: 0, WebkitTapHighlightColor: "transparent", padding: "0 10px",
                    }}
                  >{"✓"} Done</button>
                </>
              )}
            </div>
          );
        })}
        {isTargetLane && (
          <button
            onClick={() => onMoveToTier(tierNum)}
            style={{
              background: "rgba(255,107,0,0.15)",
              border: "1px dashed #FF6B00",
              borderRadius: 8,
              color: "#FF9500",
              fontSize: 11,
              fontWeight: 700,
              fontFamily: "inherit",
              padding: "8px 14px",
              cursor: "pointer",
              flexShrink: 0,
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              gap: 4,
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Move here
          </button>
        )}
        {isEmpty && !isTargetLane && (
          <span style={{ fontSize: 11, color: "#334155", fontStyle: "italic", padding: "0 8px" }}>No players</span>
        )}
      </div>
    </div>
  );
}

// --- Cheat Sheet: single position column ---
function CheatSheetColumn({ pos, players, userTierOverrides, overrideKeys, tierNames, myValues, dynamicPrices, marks, onToggleMark, lyPrices, printable, poolLimit, present, sold = null, suppressDollars = false }) {
  const { groups, pMap, tiers } = useMemo(
    () => buildPosTierData(players, pos, userTierOverrides),
    [players, pos, userTierOverrides]
  );

  // Rank slot = position in the FLAT rendered order, counting straight through
  // the tier dividers (the 1st row in the column is slot 0 regardless of which
  // tier it sits in). Precomputed rather than a counter mutated during render,
  // and it skips the same rows the render skips (missing pMap entry, empty
  // tiers) so the slot can never drift from what's on screen.
  const rankSlot = useMemo(() => {
    const m = new Map();
    let n = 0;
    for (const tier of tiers) {
      for (const id of (groups[tier.tier] || [])) {
        if (pMap[id]) m.set(id, n++);
      }
    }
    return m;
  }, [tiers, groups, pMap]);

  // Where the draftable pool ends. Past this rank buildTiers has fallen through
  // to its [Infinity, 1, 1] bucket, so every price is $1 by construction — not
  // a cheap player, a price that carries no information. The printed sheet
  // suppresses those prices and marks the edge once, at the first such row in
  // flat render order. Tier order is user-editable so this is not guaranteed to
  // be a clean prefix; the marker goes where the reader first meets it and the
  // tier blocks are left alone.
  const poolEdgeId = useMemo(() => {
    for (const tier of tiers) {
      for (const id of (groups[tier.tier] || [])) {
        const p = pMap[id];
        if (!p || !printable.has(`${pos}:${id}`)) continue;
        if (parseRankNum(getEffectivePosRank(p.name, p.posRank)) > poolLimit) return id;
      }
    }
    return null;
  }, [tiers, groups, pMap, printable, pos, poolLimit]);

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* Column header */}
      {/* --chip-color hands the inline POS_COLORS value to the print
          stylesheet, which cannot see inline styles. Inert on screen. */}
      <div className="cheat-pos-header" style={{
        "--chip-color": (POS_COLORS[pos] || { bg: "#334155" }).bg,
        padding: "6px 10px",
        background: (POS_COLORS[pos] || { bg: "#334155" }).bg,
        color: (POS_COLORS[pos] || { text: "#e2e8f0" }).text,
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: "1px",
        borderRadius: "6px 6px 0 0",
        textAlign: "center",
      }}>{pos}</div>

      {/* Print-only column key, aligned over the real cells by the shared
          .cheat-* widths. Saves the reader a trip back to the legend to find
          out what a number is. */}
      <div className="cheat-col-head">
        <span className="cheat-name" />
        <span className="cheat-team">TEAM</span>
        {present.adp && <span className="cheat-adp">ADP</span>}
        <span className="cheat-mkt">{present.mv ? "LG$" : "$"}</span>
        {present.mv && <span className="cheat-mv">YOU$</span>}
        {present.ly && <span className="cheat-ly">LY$</span>}
      </div>

      <div className="cheat-pos-card" style={{
        background: "#0f172a",
        borderRadius: "0 0 6px 6px",
        border: "1px solid rgba(255,255,255,0.06)",
        borderTop: "none",
      }}>
        {tiers.map(tier => {
          const ids = groups[tier.tier] || [];
          if (ids.length === 0) return null;
          const color = getTierColor(tier.tier);
          // Print depth is decided once in CheatSheet (the legend needs the
          // same answer, across all four positions) and handed down as a set.
          // A tier prints only if something in it does.
          const isDeep = !ids.some(id => pMap[id] && printable.has(`${pos}:${id}`));
          // cheat-tier-block: the print stylesheet keeps a tier from splitting
          // across a page. The grid's direct children are position COLUMNS,
          // not tiers — the break rule has to reach this depth.
          return (
            <div key={tier.tier} className={`cheat-tier-block${isDeep ? " cheat-tier-deep" : ""}`}>
              {/* Tier divider */}
              <div className="cheat-tier-divider" style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 8px",
                background: "rgba(255,255,255,0.02)",
                borderBottom: "1px solid rgba(255,255,255,0.04)",
                borderTop: tier.tier > 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
              }}>
                <span className="cheat-tier-chip" style={{
                  "--chip-color": color,
                  background: color,
                  color: "#fff",
                  fontSize: 8,
                  fontWeight: 800,
                  padding: "1px 4px",
                  borderRadius: 2,
                  letterSpacing: "0.3px",
                }}>T{tier.tier}</span>
                <span style={{ fontSize: 10, color: tierNames?.[`${pos}:${tier.tier}`] ? "#c8d2e0" : "#94a3b8", fontWeight: 600 }}>{tierNames?.[`${pos}:${tier.tier}`] || tier.label}</span>
              </div>

              {/* Player rows */}
              {ids.map(id => {
                const p = pMap[id];
                if (!p) return null;
                const adp = getAdpValue(p.name);
                const isOverridden = overrideKeys.has(`${pos}:${id}`);
                // Two prices, independently optional. mkt (gray) is the shared
                // league board price — the same `${name}-${pos}` lookup every
                // other surface renders. mv (orange) is the personal
                // projection-derived value. Missing values render nothing at
                // all — not an em dash: print space is precious and an empty
                // marker is pure clutter on paper.
                const priceKey = `${p.name}-${pos}`;
                const mkt = suppressDollars ? undefined : dynamicPrices?.get(priceKey);
                const mv = suppressDollars ? null : (myValues ? myValues[draftValueKey(p.name, pos)] : null);
                // Draft mark rides the same composite key as the price.
                const mark = marks[priceKey];
                // 🔴 TWO MEANINGS, ONE STRIKE-THROUGH. A manual mark is "not
                // interested"; a sale is "he went for $X". Both cross the row
                // off, only the second carries a number, and resetMarks touches
                // only the first.
                const soldFor = sold ? sold.get(priceKey) : undefined;
                const isSold = soldFor !== undefined;
                const isGone = mark === "gone" || isSold;
                const isMine = mark === "mine" && !isSold;
                // What this room paid for the Nth-best player at this position
                // last year — a slot price, not a player price.
                const ly = lyPrices?.[pos]?.[rankSlot.get(id)];
                return (
                  <React.Fragment key={id}>
                  {id === poolEdgeId && (
                    <div className="cheat-pool-edge">remaining players: $1</div>
                  )}
                  <div
                    onClick={() => onToggleMark(priceKey)}
                    data-testid={`cheat-row:${priceKey}`}
                    className={`cheat-row${isMine ? " cheat-row-mine" : ""}${isGone ? " cheat-row-gone" : ""}${printable.has(`${pos}:${id}`) ? "" : " cheat-row-deep"}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "3px 8px",
                      borderLeft: isOverridden ? "2px solid #FF6B00" : "2px solid transparent",
                      borderBottom: "1px solid rgba(255,255,255,0.02)",
                      cursor: "pointer",
                      // Fast repeated clicking during a live auction must not
                      // start selecting the column as text.
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      WebkitTapHighlightColor: "transparent",
                      background: isMine ? "rgba(255,107,0,0.12)" : undefined,
                      opacity: isGone ? 0.45 : undefined,
                    }}
                  >
                    <span className="cheat-name" style={{
                      fontSize: 11,
                      fontWeight: isMine ? 700 : 600,
                      color: isMine ? "#FF9500" : "#e2e8f0",
                      flex: 1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      minWidth: 0,
                      textDecoration: isGone ? "line-through" : undefined,
                    }}>
                      {/* Inline here (not absolute): the name cell is flex:1
                          with ellipsis, so badges eat name width rather than
                          widening the row or the column. */}
                      <PlayerBadges name={p.name} style={{ marginRight: 3, verticalAlign: "middle" }} />
                      {p.name}
                    </span>
                    {/* What the room actually paid. Rendered outside the
                        struck-through name so the number stays readable, and
                        in green so a sale is never mistaken for the gray
                        league price or the orange personal value. */}
                    {isSold && (
                      <span className="cheat-sold" style={{
                        fontSize: 10, fontWeight: 800, color: "#34d399", flexShrink: 0,
                      }}>${soldFor}</span>
                    )}
                    {/* Every optional cell that exists ANYWHERE on the sheet
                        renders in every row — as the value, or as an empty
                        print-only placeholder. Without that a row missing its
                        ADP would slide its $ left and break alignment under
                        the column header. The placeholders are display:none on
                        screen, so the screen layout is untouched. */}
                    <span className="cheat-team" style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, flexShrink: 0 }}>{p.team}</span>
                    {present.adp && (adp
                      ? <span className="cheat-adp" style={{ fontSize: 10, color: "#94a3b8", flexShrink: 0 }}>{adp}</span>
                      : <span className="cheat-adp cheat-cell-empty" />)}
                    {mkt != null
                      ? <span className="cheat-mkt" style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", flexShrink: 0 }}>${mkt}</span>
                      : <span className="cheat-mkt cheat-cell-empty" />}
                    {present.mv && (mv != null
                      ? <span className="cheat-mv" style={{ fontSize: 10, fontWeight: 700, color: "#FF9500", flexShrink: 0 }}>${mv}</span>
                      : <span className="cheat-mv cheat-cell-empty" />)}
                    {present.ly && (ly != null
                      ? <span className="cheat-ly" style={{ fontSize: 9, fontWeight: 600, color: "#7dd3fc", flexShrink: 0 }}>LY ${ly}</span>
                      : <span className="cheat-ly cheat-cell-empty" />)}
                  </div>
                  </React.Fragment>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Cheat Sheet view ---
/**
 * The manual draft marks, and their per-league persistence.
 *
 * Extracted so the Live Board can drive the same sheet without duplicating the
 * storage key or the none -> gone -> mine -> none cycle. Remounted per league by
 * the `key` at each call site, so the initializer always reads the right key and
 * stale marks can never be persisted under a league they don't belong to.
 */
export function useCheatMarks(activeLeagueName) {
  const [marks, setMarks] = useState(() => readCheatMarks(activeLeagueName));
  useEffect(() => {
    try {
      localStorage.setItem(cheatMarksKey(activeLeagueName), JSON.stringify(marks));
    } catch { /* quota / private mode — marks stay in-session */ }
  }, [marks, activeLeagueName]);

  // none → gone → mine → none. Delete rather than store a falsy value so the
  // counts below stay a simple tally of what's in the object.
  const toggleMark = useCallback((key) => {
    setMarks(prev => {
      const next = { ...prev };
      if (!prev[key]) next[key] = "gone";
      else if (prev[key] === "gone") next[key] = "mine";
      else delete next[key];
      return next;
    });
  }, []);

  const { goneCount, mineCount } = useMemo(() => {
    let gone = 0, mine = 0;
    for (const v of Object.values(marks)) {
      if (v === "gone") gone++;
      else if (v === "mine") mine++;
    }
    return { goneCount: gone, mineCount: mine };
  }, [marks]);

  // 🔴 CLEARS THE MANUAL MARKS ONLY. On the Live Board a sold row is struck
  // through because the auction says so, not because anyone clicked it — that
  // is a fact, not a preference, and this must never wipe it. It cannot: sold
  // comes from board.sales via the `sold` prop and never enters `marks`.
  const resetMarks = useCallback(() => {
    if (!window.confirm("Clear all draft marks?")) return;
    setMarks({});
    try { localStorage.removeItem(cheatMarksKey(activeLeagueName)); } catch { /* no-op */ }
  }, [activeLeagueName]);

  return { marks, toggleMark, resetMarks, goneCount, mineCount };
}

/**
 * CheatSheet — the Tier Builder's wrapper. Owns nothing but the two things that
 * need a provider or local storage, and hands the rest to the pure view.
 *
 * 🔴 THE SPLIT EXISTS BECAUSE OF usePlayerCard. PlayerCardContext is
 * createContext(null), so `const { dynamicPrices } = usePlayerCard()`
 * DESTRUCTURES NULL AND THROWS anywhere outside PlayerCardProvider — and the
 * /watch spectator route mounts beside /scout, above that provider. Hooks
 * cannot be called conditionally, so the fix is not an optional read: the
 * context call has to live in a component the provider-less surfaces never
 * render. That component is this one. CheatSheetView below takes prices as a
 * prop and touches no context at all.
 */
function CheatSheet({ players, settings, userTierOverrides, overrideKeys, tierNames, activeLeagueName }) {
  const { dynamicPrices } = usePlayerCard();
  const markState = useCheatMarks(activeLeagueName);
  return (
    <CheatSheetView
      players={players} settings={settings} userTierOverrides={userTierOverrides}
      overrideKeys={overrideKeys} tierNames={tierNames} activeLeagueName={activeLeagueName}
      dynamicPrices={dynamicPrices} {...markState}
    />
  );
}

/**
 * CheatSheetView — the sheet itself. Pure: every input is a prop.
 *
 * `sold` is the Live Board's contribution — a Map of `${name}-${pos}` to the
 * price the room actually paid. A row is struck through if it is manually
 * marked gone OR sold, and the two are deliberately distinguishable: a manual
 * mark says "not interested", a sale says "he went for $X" and prints the
 * number.
 */
/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 A COLUMN WITH NO TIERS — DEF AND K ON THE LIVE BOARD.
 *
 * CheatSheetColumn is built around `buildPosTierData`: tier groups, tier
 * dividers, a rank slot per row, a pool edge. DEF and K have none of that.
 * TIER_MAPS has no bands for them, ADP_DATA has no rows for them, and inventing
 * either would be publishing an opinion the app does not hold.
 *
 * So this renders the SAME head, the SAME row, the SAME sold/price/strike
 * behaviour, over one alphabetical list with no dividers. Everything a reader
 * can see matches the four tier columns; only the thing that does not exist is
 * missing.
 *
 * 🔴 THE CALLER SUPPLIES THE ROWS. `extraColumns: [{pos, rows}]` — the Live
 * Board hands over PlayersLeft's own pool, so the BOARD tab and the PLAYERS
 * LEFT tab cannot disagree about who exists. This component knows nothing about
 * where a defense list comes from.
 *
 * ⚠️ NOT MARKABLE, DELIBERATELY. `onToggleMark` is not wired here. The marks
 * are the user's tier-sheet marks, keyed per league and reset together; a
 * kicker crossed off by hand would sit in that store forever with no column in
 * the Tier Builder to clear it from. Sales still strike these rows, which is
 * the behaviour anyone actually wants during a draft.
 * ══════════════════════════════════════════════════════════════════════
 */
function CheatSheetExtraColumn({ pos, rows, dynamicPrices, sold, suppressDollars, present }) {
  const left = rows.filter(p => !(sold && sold.has(p.name + "-" + pos))).length;
  return (
    <div className="cheat-extra-col" data-testid={"cheat-extra-col-" + pos} style={{ flex: 1, minWidth: 0 }}>
      {/* Same head as CheatSheetColumn, plus the count. --chip-color is what
          the print stylesheet reads; it cannot see inline styles. */}
      <div className="cheat-pos-header" style={{
        "--chip-color": (POS_COLORS[pos] || { bg: "#334155" }).bg,
        padding: "6px 10px",
        background: (POS_COLORS[pos] || { bg: "#334155" }).bg,
        color: (POS_COLORS[pos] || { text: "#e2e8f0" }).text,
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: "1px",
        borderRadius: "6px 6px 0 0",
        textAlign: "center",
      }}>{pos} <span style={{ fontWeight: 600, opacity: 0.8 }}>left: {left}</span></div>

      <div className="cheat-col-head">
        <span className="cheat-name" />
        <span className="cheat-team">TEAM</span>
        {present.adp && <span className="cheat-adp" />}
        <span className="cheat-mkt">{present.mv ? "LG$" : "$"}</span>
        {present.mv && <span className="cheat-mv" />}
        {present.ly && <span className="cheat-ly" />}
      </div>

      <div className="cheat-pos-card" style={{
        background: "#0f172a",
        borderRadius: "0 0 6px 6px",
        border: "1px solid rgba(255,255,255,0.06)",
        borderTop: "none",
      }}>
        {rows.map(p => {
          const priceKey = p.name + "-" + pos;
          const mkt = suppressDollars ? undefined : (dynamicPrices ? dynamicPrices.get(priceKey) : undefined);
          const soldFor = sold ? sold.get(priceKey) : undefined;
          const isSold = soldFor !== undefined;
          return (
            <div
              key={priceKey}
              data-testid={"cheat-row:" + priceKey}
              className={"cheat-row" + (isSold ? " cheat-row-gone" : "")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "3px 8px",
                borderLeft: "2px solid transparent",
                borderBottom: "1px solid rgba(255,255,255,0.02)",
                userSelect: "none",
                WebkitUserSelect: "none",
                opacity: isSold ? 0.45 : undefined,
              }}
            >
              <span className="cheat-name" style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#e2e8f0",
                flex: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
                textDecoration: isSold ? "line-through" : undefined,
              }}>{p.name}</span>
              {isSold && (
                <span className="cheat-sold" style={{
                  fontSize: 10, fontWeight: 800, color: "#34d399", flexShrink: 0,
                }}>${soldFor}</span>
              )}
              <span className="cheat-team" style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, flexShrink: 0 }}>{p.team}</span>
              {present.adp && <span className="cheat-adp cheat-cell-empty" />}
              {mkt != null
                ? <span className="cheat-mkt" style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", flexShrink: 0 }}>${mkt}</span>
                : <span className="cheat-mkt cheat-cell-empty" />}
              {present.mv && <span className="cheat-mv cheat-cell-empty" />}
              {present.ly && <span className="cheat-ly cheat-cell-empty" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CheatSheetView({
  players, settings, userTierOverrides, overrideKeys, tierNames, activeLeagueName,
  dynamicPrices, marks, toggleMark, resetMarks, goneCount, mineCount,
  sold = null, suppressDollars = false, formatNote = null,
  /**
   * 🔴 OPTIONAL, AND ABSENT IS THE TIER BUILDER'S CASE. `[{pos, rows}]` —
   * tier-less columns appended to the same grid. The Live Board passes DEF and
   * K; nothing else passes anything.
   *
   * When it is absent this component renders the tree it always did, down to
   * the DOM: no wrappers, four grid children, the print table unchanged. That
   * is asserted by a snapshot of the rendered column heads, because "the Tier
   * Builder is untouched" is the kind of claim that quietly stops being true.
   */
  extraColumns = null,
  /**
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 OPTIONAL, AND ABSENT IS THE TIER BUILDER'S CASE AGAIN.
   *
   * The sheet is capped at 900px and centred, which is right on the Tier
   * Builder page (a reading-width document among other page furniture) and
   * wrong on the Live Board, where the sheet IS the screen. At 2560px the six
   * columns sat in the middle third with names cut to eight characters and
   * two-thirds of the monitor empty.
   *
   * `fluid` drops the cap so the sheet spans its parent — on /live that is the
   * board's own content container, the same width as the WAITING ON panel
   * above it. It does NOT introduce a width of its own: there is one place
   * that decides how wide the board is and this is not it.
   *
   * ⚠️ IT ALSO SWITCHES 1fr TO minmax(0, 1fr). A bare `1fr` track has
   * `min-width: auto`, so a column cannot shrink below its longest unbreakable
   * row — with `white-space: nowrap` names that is the whole point of the
   * ellipsis defeated, and six of them can push the grid wider than its
   * container. Only under `fluid`, because changing it unconditionally would
   * alter the Tier Builder's rendered style string.
   * ══════════════════════════════════════════════════════════════════════
   */
  fluid = false,
}) {
  // Read once on mount — the snapshot is stable while the sheet is open.
  const [mySnap] = useState(() => readMyDraftValues(activeLeagueName));
  const myValues = mySnap?.values || null;
  // Same keyed-remount deal: a league switch re-reads this league's own record.
  const [lyPrices] = useState(() => readLyPrices(activeLeagueName));

  // How deep the printed sheet runs, per position. Read from the SAME helper
  // that builds the price tiers — the fractions live in usePricing and only
  // there.
  const draftedCounts = useMemo(
    () => computeDraftedCounts({ positions: settings.positions, teams: settings.teams }),
    [settings.positions, settings.teams]
  );
  const printDepth = useMemo(
    () => computePrintDepth({ positions: settings.positions, teams: settings.teams }),
    [settings.positions, settings.teams]
  );

  // Decided here rather than per column because the legend has to describe
  // what actually survived across ALL FOUR positions. Computing it per column
  // would let a column that prints nothing still claim its columns exist.
  const printPlan = useMemo(() => {
    const printable = new Set();
    let hasAdp = false, hasMv = false, hasLy = false;
    for (const pos of POSITIONS) {
      const { groups, pMap, tiers } = buildPosTierData(players, pos, userTierOverrides);
      const limit = printDepth[pos] || 0;
      // Walks tiers→ids in the same order the column renders, so `slot` is the
      // identical flat rank the LY lookup uses there. Any drift here and the
      // legend would advertise an LY column the rows never show.
      let slot = 0;
      for (const tier of tiers) {
        for (const id of (groups[tier.tier] || [])) {
          const p = pMap[id];
          if (!p) continue;
          const mySlot = slot++;
          const key = `${p.name}-${pos}`;
          const mkt = dynamicPrices?.get(key) ?? 0;
          const mv = myValues ? (myValues[draftValueKey(p.name, pos)] ?? null) : null;
          const rank = parseRankNum(getEffectivePosRank(p.name, p.posRank));
          // Inside the league's draftable pool, OR the user rates them
          // regardless of what the market says.
          if (rank > limit && Math.max(mkt, mv ?? 0) < PRINT_MIN_PRICE) continue;
          printable.add(`${pos}:${id}`);
          if (getAdpValue(p.name)) hasAdp = true;
          if (mv != null) hasMv = true;
          if (lyPrices?.[pos]?.[mySlot] != null) hasLy = true;
        }
      }
    }
    return { printable, hasAdp, hasMv, hasLy };
  }, [players, userTierOverrides, printDepth, dynamicPrices, myValues, lyPrices]);

  // Which optional cells exist anywhere on the printed sheet. Drives both the
  // per-column header and the row placeholders that keep it aligned. Built from
  // what actually rendered — a header naming a column the sheet does not have
  // reads as a bug, and most users have neither saved values nor imported
  // history.
  const printCells = useMemo(() => ({
    adp: printPlan.hasAdp, mv: printPlan.hasMv, ly: printPlan.hasLy,
  }), [printPlan]);

  const extraCols = Array.isArray(extraColumns) ? extraColumns : [];

  const tierCols = POSITIONS.map(pos => (
    <CheatSheetColumn
      key={pos}
      pos={pos}
      players={players}
      userTierOverrides={userTierOverrides}
      overrideKeys={overrideKeys}
      tierNames={tierNames}
      myValues={myValues}
      dynamicPrices={dynamicPrices}
      marks={marks}
      onToggleMark={toggleMark}
      lyPrices={lyPrices}
      printable={printPlan.printable}
      poolLimit={draftedCounts[pos]}
      present={printCells}
      sold={sold}
      suppressDollars={suppressDollars}
    />
  ));

  return (
    <div
      className={fluid ? "tier-cheat-sheet tier-cheat-sheet-fluid" : "tier-cheat-sheet"}
      data-testid="cheat-sheet"
      style={fluid ? { maxWidth: "none", margin: 0 } : { maxWidth: 900, margin: "0 auto" }}
    >
      <div className="tier-cheat-sheet-no-print" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500 }}>
          <span style={{ color: "#94a3b8", fontWeight: 600 }}>gray $</span> = league price
          {" · "}
          <span style={{ color: "#FF9500", fontWeight: 700 }}>orange $</span> = your value
          {lyPrices && (
            <>
              {" · "}
              <span style={{ color: "#7dd3fc", fontWeight: 600 }}>blue</span> = this room last year (rank slot)
            </>
          )}
          {" · "}
          click = gone · again = mine · again = clear
        </span>
        {(goneCount > 0 || mineCount > 0) && (
          <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b" }}>
            <span style={{ color: "#94a3b8" }}>{goneCount} gone</span>
            {" · "}
            <span style={{ color: "#FF9500" }}>{mineCount} mine</span>
          </span>
        )}
        {(goneCount > 0 || mineCount > 0) && (
          <button
            onClick={resetMarks}
            style={{
              background: "transparent",
              border: "1px solid rgba(148,163,184,0.25)",
              borderRadius: 6,
              color: "#94a3b8",
              fontSize: 10,
              fontWeight: 600,
              fontFamily: "inherit",
              padding: "3px 8px",
              cursor: "pointer",
            }}
          >Reset marks</button>
        )}
        <span style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.5px", fontWeight: 500 }}>Optimized for screenshots &amp; print</span>
        {formatNote && (
          <span style={{ fontSize: 10, color: "#64748b", fontWeight: 500, flexBasis: "100%", textAlign: "right" }}>{formatNote}</span>
        )}
      </div>
      {/* 🔴 THE COLUMN COUNT FOLLOWS THE PROP. Four with no extras, which is
          every caller but the Live Board; six with DEF and K. The responsive
          rules below key on this container either way, so 820px still drops to
          two columns (DEF and K become the next row) and 480px to one. */}
      <div className={extraCols.length ? "cheat-grid cheat-grid-split" : "cheat-grid"} style={{
        display: "grid",
        gridTemplateColumns: `repeat(${POSITIONS.length + extraCols.length}, ${fluid ? "minmax(0, 1fr)" : "1fr"})`,
        gap: 10,
        alignItems: "start",
      }}>
        {/* 🔴 NO WRAPPER WHEN THERE ARE NO EXTRAS. `tierCols` is the four
            columns themselves in that case, so the DOM, the print table and
            the snapshot are byte-identical to before this prop existed. With
            extras they are wrapped, because print has to put the four in one
            table and the other two on their own page — see the print rules. */}
        {extraCols.length === 0 ? tierCols : (
          <>
            <div className="cheat-tier-cols">{tierCols}</div>
            <div className="cheat-extra-cols">
              {extraCols.map(col => (
                <CheatSheetExtraColumn
                  key={col.pos}
                  pos={col.pos}
                  rows={col.rows || []}
                  dynamicPrices={dynamicPrices}
                  sold={sold}
                  suppressDollars={suppressDollars}
                  present={printCells}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Inline responsive styles */}
      <style>{`
        @media (max-width: 820px) {
          .tier-cheat-sheet > div:last-of-type { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 480px) {
          .tier-cheat-sheet > div:last-of-type { grid-template-columns: 1fr !important; }
        }
        /* 🔴 THE WRAPPERS ARE NOT BOXES ON SCREEN. display:contents lifts
           their children into the grid, so the six columns are six grid items
           in one container, all the same width, and the two media queries
           above wrap them exactly as they wrap four. The wrappers exist only
           so PRINT can treat the two groups differently. Absent when there are
           no extra columns. */
        .cheat-tier-cols, .cheat-extra-cols { display: contents; }

        /* Screen: the print-only key and the pool-edge rule are not part of the
           layout at all. Screen keeps every price, including the $1 tail. */
        .cheat-pool-edge, .cheat-col-head, .cheat-cell-empty { display: none; }

        @page { margin: 8mm; }

        @media print {
          /* ---- 1. Isolate the sheet -------------------------------------
             The app header and the TEAM BUILDER / TIERS / MOCK DRAFT /
             ALMANAC tab bar live outside TierBuilder and carry no no-print
             class, so they otherwise print ahead of it. Safe to pin to top:0
             — nothing in the ancestor chain (.app-root, #root) is positioned
             or transformed, so this resolves to the top of page 1. */
          /* "body *" hides every DESCENDANT but never matches body or
             html themselves, and common.css:6 paints both #0a0f1a. The root
             element's background propagates to the page canvas, so with
             "Background graphics" ON the whole sheet printed on a dark slab.
             Repainting both white is the actual fix. */
          html, body {
            background: #fff !important;
            background-color: #fff !important;
          }
          body * { visibility: hidden; }
          .tier-cheat-sheet, .tier-cheat-sheet * { visibility: visible; }
          .tier-cheat-sheet {
            position: absolute; left: 0; top: 0;
            width: 100%; max-width: 100% !important;
            --mine-color: #FF6B00;
          }
          .tier-cheat-sheet-no-print { display: none !important; }

          /* Depth cutoff, computed in the render (CSS cannot see prices or
             ranks). Rows outside the league's draftable pool drop out, and a
             tier drops out once nothing in it survives. Screen is untouched. */
          .cheat-row-deep, .cheat-tier-deep { display: none !important; }

          /* ---- 2. White page --------------------------------------------
             Browsers drop backgrounds when printing but KEEP text colour, and
             this is a dark theme — near-white names on white paper. Kill every
             fill and force dark text, so the sheet reads identically whether
             the user has "Background graphics" on or off. Must precede the
             chip rules below so those win on source order. */
          .tier-cheat-sheet, .tier-cheat-sheet * {
            background: transparent !important;
            background-color: transparent !important;
            box-shadow: none !important;
            text-shadow: none !important;
            color: #111 !important;
          }
          /* Explicit white, not transparent — MUST follow the blanket rule
             above (equal specificity, later wins). Transparent lets whatever
             is beneath show through, which is the same failure one layer up. */
          .tier-cheat-sheet { background: #fff !important; }
          .cheat-adp { color: #555 !important; }
          .cheat-pos-card { border: none !important; }

          /* ---- 3. Chips: colour without fill ----------------------------
             The chip colours are inline styles from POS_COLORS/getTierColor,
             which print CSS cannot read — the render passes them out through
             --chip-color. Outline + coloured text keeps the position coding
             that makes the sheet scannable at a fraction of the ink, and
             borders/text survive "Background graphics: off" where a fill
             would not. */
          .cheat-pos-header, .cheat-tier-chip {
            background: transparent !important;
            color: var(--chip-color) !important;
            border: 1px solid var(--chip-color) !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* ---- 4. Marks, unfilled --------------------------------------- */
          .cheat-row-mine {
            border-left: 3px solid var(--mine-color) !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .cheat-row-mine, .cheat-row-mine * { font-weight: 700 !important; }
          /* opacity prints muddy — a flat grey is cleaner and cheaper. */
          .cheat-row-gone { opacity: 1 !important; text-decoration: line-through !important; }
          .cheat-row-gone, .cheat-row-gone * { color: #777 !important; }

          /* ---- 5. Pack it ----------------------------------------------
             Multi-column, not the screen grid: multicol flows tier blocks
             continuously into the next column, so a short QB list does not
             leave three-quarters of a page blank the way four fixed grid
             columns do. column-fill:auto fills each column to the page height
             before starting the next. */
          /* One position per column, each continuing in the SAME column
             position on page 2 — the reader always knows where RB is without
             scanning. Multicol cannot do this (it flows one column into the
             next, which is what orphaned TE onto a near-empty page 2).
             display:table is the primitive that can: a table row taller than
             the page fragments, and each cell resumes in its own column.
             Chrome fragments grid less reliably than tables, which is why this
             is a table and not the screen grid. */
          .tier-cheat-sheet > div:last-of-type {
            display: table !important;
            width: 100% !important;
            table-layout: fixed;
            /* collapse + per-cell padding rather than border-spacing: the
               latter also pads the OUTER edges, throwing away ~23px of usable
               width for nothing. Every pixel here comes off the name column. */
            border-collapse: collapse;
            column-count: auto !important;
            gap: 0 !important;
          }
          .tier-cheat-sheet > div:last-of-type > div {
            display: table-cell !important;
            vertical-align: top;
            padding-right: 3mm;
          }
          .tier-cheat-sheet > div:last-of-type > div:last-child { padding-right: 0; }

          /* ---- 5c. SIX COLUMNS ON SCREEN, FOUR ON THE PAGE -------------
             🔴 MEASURED, NOT PREFERRED. The print sheet is table-layout:
             fixed, so N columns each get 1/N of ~199mm of usable width. At
             four that is ~189px per column and the name cell keeps ~95px after
             TEAM/ADP/$ and their gaps. At six it is ~126px per column and the
             name cell is left ~32px, which at 7.5pt is four characters: every
             name on the sheet becomes an ellipsis. So the screen is six
             columns and the PAGE is four plus a second page for DEF and K,
             which is the fallback the brief authorised.

             Scoped to .cheat-grid-split, which only exists when extraColumns
             is passed. With no extras there is no wrapper, these selectors
             match nothing, and the printed Tier Builder sheet is the same
             bytes it was. */
          .tier-cheat-sheet > div:last-of-type.cheat-grid-split {
            display: block !important;
          }
          .cheat-grid-split > .cheat-tier-cols,
          .cheat-grid-split > .cheat-extra-cols {
            display: table !important;
            width: 100% !important;
            table-layout: fixed;
            border-collapse: collapse;
          }
          .cheat-grid-split > div > div {
            display: table-cell !important;
            vertical-align: top;
            padding-right: 3mm;
          }
          .cheat-grid-split > div > div:last-child { padding-right: 0; }
          /* DEF and K start the page after the tier sheet. Two columns of
             ~33 rows on a page of their own, at full name width. */
          .cheat-grid-split > .cheat-extra-cols {
            break-before: page;
            page-break-before: always;
          }
          /* Tier blocks SPLIT across pages. They used to be break-inside:avoid,
             from when a tier was a handful of rows; at current depth a deep
             bench tier is taller than whatever column space is left, so it got
             moved whole and the column just ended — ~350px of page 1 wasted per
             column. Splitting costs a tier being cut in two and buys back most
             of a page.
             orphans/widows are declared for intent but see the note below. */
          .cheat-tier-block { orphans: 3; widows: 3; }
          /* This is the rule that actually does work on block children: a row
             can never be cut through the middle horizontally. */
          .cheat-row { break-inside: avoid; page-break-inside: avoid; }
          /* A tier label must never strand alone at the foot of a column. */
          .cheat-tier-divider {
            break-after: avoid;
            page-break-after: avoid;
          }
          /* Never strand a position header at the foot of a column. */
          .cheat-pos-header {
            break-after: avoid;
            page-break-after: avoid;
            font-size: 8pt !important;
            padding: 1px 4px !important;
            letter-spacing: 0.5px !important;
            margin-top: 2px;
          }
          .cheat-tier-divider { padding: 1px 4px !important; }
          .cheat-tier-divider * { font-size: 6.5pt !important; }
          .cheat-tier-chip { font-size: 6pt !important; padding: 0 2px !important; }
          .cheat-row { padding: 1px 2px !important; gap: 5px !important; }
          .cheat-row, .cheat-row * {
            font-size: 7.5pt !important;
            line-height: 1.15 !important;
          }

          /* ---- 5a. Column key + cell alignment -------------------------
             Fixed widths shared by the header and every data cell. Without
             them the numbers are content-sized and nothing lines up under a
             header. */
          .cheat-col-head {
            display: flex !important;
            align-items: center;
            gap: 5px;
            padding: 1px 2px !important;
            font-size: 6pt !important;
            font-weight: 700;
            color: #555 !important;
            border-bottom: 1px solid #999;
            break-after: avoid;
            page-break-after: avoid;
          }
          /* Labels CENTRED over their cells; data stays right-aligned, which is
             correct for numbers. The cells are sized for the wider of the two
             (TEAM is wider than LAC, 148.7 is wider than ADP) so neither the
             label nor the value crowds its neighbour. */
          .cheat-col-head .cheat-team,
          .cheat-col-head .cheat-adp,
          .cheat-col-head .cheat-mkt,
          .cheat-col-head .cheat-mv,
          .cheat-col-head .cheat-ly { text-align: center !important; }

          .cheat-cell-empty { display: block !important; }
          .cheat-name { flex: 1 !important; min-width: 0 !important; }
          .cheat-team, .cheat-adp, .cheat-mkt, .cheat-mv, .cheat-ly {
            flex: 0 0 auto !important;
            text-align: right;
          }
          .cheat-team { width: 22px; }
          .cheat-adp  { width: 21px; }
          .cheat-mkt  { width: 20px; }
          .cheat-mv   { width: 20px; }
          .cheat-ly   { width: 30px; }

          /* ---- 5b. End of the draftable pool ---------------------------
             Past this rank buildTiers has fallen through to [Infinity, 1, 1],
             so every price below is $1. The prices still print — knowing a
             player IS $1 is worth the ink — but the boundary is marked once
             per position so the reader knows where the draftable pool ended
             rather than having to infer it. */
          .cheat-pool-edge {
            display: block !important;
            border-top: 1px solid #999;
            font-size: 6pt !important;
            line-height: 1.1 !important;
            color: #777 !important;
            text-align: right;
            padding: 1px 4px 0 !important;
            break-after: avoid;
            page-break-after: avoid;
          }

        }
      `}</style>
    </div>
  );
}

// --- Mobile scroll-for-more indicator ---
function ScrollMoreIndicator() {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      const nearBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 120;
      setVisible(!nearBottom);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;
  return (
    <div className="tier-scroll-more" style={{
      position: "fixed",
      bottom: 12,
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      alignItems: "center",
      gap: 4,
      background: "rgba(15,23,42,0.85)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 20,
      padding: "6px 14px",
      fontSize: 10,
      color: "#94a3b8",
      fontWeight: 500,
      letterSpacing: "0.3px",
      pointerEvents: "none",
      zIndex: 10,
      animation: "tierScrollPulse 2s ease-in-out infinite",
    }}>
      <span style={{ fontSize: 12 }}>{"\u25BC"}</span> Scroll for more tiers
    </div>
  );
}

// --- Styles (injected once) ---
function TierMobileStyles() {
  return (
    <style>{`
      /* Desktop: swim lanes are horizontal (label left, cards right) */
      .tier-lane {
        display: flex;
        align-items: stretch;
      }
      .tier-lane-label {
        width: 140px;
        min-width: 140px;
        border-right: 3px solid currentColor;
        border-bottom: none;
      }
      /* Desktop: wrap pills instead of horizontal scroll */
      .tier-lane-cards {
        flex-wrap: wrap !important;
        overflow-x: visible !important;
        overflow-y: visible !important;
        height: auto !important;
        align-items: flex-start !important;
      }
      .tier-pill {
        /* NOTE: min/max-width kept at 110/150. The wider 172/210 range (d7b2f1b,
           to stop posRank/ADP clipping) made the sortable node's measured rect
           content-variable during a drag, driving dnd-kit's useRect into an
           infinite measure→setState loop (React #185 crash on cross-tier drag).
           Re-solve the clipping without changing the sortable's width behavior. */
        min-width: 110px;
        max-width: 150px;
        width: auto;
        flex-shrink: 0;
        cursor: grab;
      }
      .tier-pill:active {
        cursor: grabbing;
      }
      /* Hide grip icons on desktop */
      .tier-grip, .tier-label-grip {
        display: none;
      }

      /* Scroll-more indicator pulse */
      @keyframes tierScrollPulse {
        0%, 100% { opacity: 0.7; transform: translateX(-50%) translateY(0); }
        50%      { opacity: 1;   transform: translateX(-50%) translateY(-3px); }
      }
      /* Hide scroll-more on desktop */
      .tier-scroll-more { display: none !important; }

      /* Pulse animation for first-time hint on T1/T2 */
      @keyframes tierPulse {
        0%   { box-shadow: 0 0 0 0 rgba(255,107,0,0.25); }
        50%  { box-shadow: 0 0 0 4px rgba(255,107,0,0.12); }
        100% { box-shadow: 0 0 0 0 rgba(255,107,0,0); }
      }
      .tier-lane-pulse {
        animation: tierPulse 1.5s ease 0.3s 2;
      }

      /* Smooth transition for sortable wrappers settling after drop */
      .tier-lane-cards > div {
        transition: transform 0.2s ease;
      }

      @media (max-width: 600px) {
        /* Tab bar: position tabs fill row equally */
        .tier-tab-bar {
          flex-wrap: wrap !important;
        }
        .tier-tab-bar .tier-pos-tab {
          flex: 1 1 0 !important;
          padding: 8px 0 !important;
          font-size: 11px !important;
          text-align: center;
          min-width: 0 !important;
        }
        .tier-tab-bar .tier-tab-divider {
          display: none !important;
        }
        .tier-tab-bar .tier-cheatsheet-group {
          flex: 1 1 100% !important;
          order: 10;
          justify-content: center !important;
          margin-left: 0 !important;
        }
        .tier-tab-bar .tier-cheatsheet-tab {
          flex: 1 !important;
          padding: 8px 0 !important;
          font-size: 11px !important;
          text-align: center;
        }

        /* Swim lanes: vertical stack (label on top, cards below) */
        .tier-lane {
          flex-direction: column !important;
        }
        .tier-lane-label {
          width: auto !important;
          min-width: 0 !important;
          border-right: none !important;
          border-bottom: 3px solid currentColor;
          padding: 8px 10px !important;
        }

        /* Cards: flex-wrap grid instead of horizontal scroll */
        .tier-lane-cards {
          flex-wrap: wrap !important;
          overflow-x: visible !important;
          gap: 6px !important;
          padding: 10px 12px !important;
        }

        /* Show grip icons on mobile */
        .tier-grip, .tier-label-grip {
          display: inline !important;
        }
        /* Show scroll-more indicator on mobile */
        .tier-scroll-more {
          display: flex !important;
        }

        /* Pills: single column, full width for readable names */
        .tier-pill {
          width: 100% !important;
          min-width: 0 !important;
          max-width: 100% !important;
          flex: 1 1 100% !important;
          min-height: 40px !important;
          padding: 8px 10px !important;
          box-sizing: border-box;
        }
        /* Prevent ADP clipping */
        .tier-adp { white-space: nowrap; }
      }
    `}</style>
  );
}

const TierBuilder = React.memo(function TierBuilder({ players, settings, activeLeagueName, watchlist, avoids, onOpenSettings }) {
  const leagueRef = useRef(activeLeagueName);
  const [activePos, setActivePos] = useState("RB");
  const [viewMode, setViewMode] = useState("editor"); // "editor" | "cheatsheet"
  const [activeId, setActiveId] = useState(null);
  const [userTierOverrides, setUserTierOverrides] = useState(() => loadOverrides(activeLeagueName));
  const [tierOrder, setTierOrder] = useState(() => loadTierOrder(activeLeagueName));
  const [tierNames, setTierNames] = useState(() => loadTierNames(activeLeagueName));
  const [showPulse, setShowPulse] = useState(() => Object.keys(loadOverrides(activeLeagueName)).length === 0);
  const [selectedPlayer, setSelectedPlayer] = useState(null); // mobile tap-to-move: { name, pos, currentTier }
  const [isMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);
  const [showSaved, setShowSaved] = useState(false);
  const [showResetMenu, setShowResetMenu] = useState(false);
  const [confirmReset, setConfirmReset] = useState(null); // null | "pos" | "all"
  const [hasEverChanged, setHasEverChanged] = useState(() => Object.keys(loadOverrides(activeLeagueName)).length > 0 || Object.keys(loadTierOrder(activeLeagueName)).length > 0 || Object.keys(loadTierNames(activeLeagueName)).length > 0);
  const savedTimerRef = useRef(null);

  // Reload tier data when active league changes
  useEffect(() => {
    if (activeLeagueName === leagueRef.current) return;
    leagueRef.current = activeLeagueName;
    const o = loadOverrides(activeLeagueName);
    const ord = loadTierOrder(activeLeagueName);
    const n = loadTierNames(activeLeagueName);
    setUserTierOverrides(o);
    setTierOrder(ord);
    setTierNames(n);
    setHasEverChanged(Object.keys(o).length > 0 || Object.keys(ord).length > 0 || Object.keys(n).length > 0);
    setShowPulse(Object.keys(o).length === 0);
    setSelectedPlayer(null);
  }, [activeLeagueName]);

  const triggerSaved = useCallback(() => {
    setShowSaved(true);
    setHasEverChanged(true);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setShowSaved(false), 1500);
  }, []);

  // Clear selection when switching positions
  useEffect(() => { setSelectedPlayer(null); }, [activePos]);

  // Persist overrides to localStorage on every change
  useEffect(() => {
    saveOverrides(userTierOverrides, activeLeagueName);
  }, [userTierOverrides, activeLeagueName]);

  // Persist tier order to localStorage on every change
  useEffect(() => {
    saveTierOrder(tierOrder, activeLeagueName);
  }, [tierOrder, activeLeagueName]);

  // Persist tier names to localStorage on every change
  useEffect(() => {
    saveTierNames(tierNames, activeLeagueName);
  }, [tierNames, activeLeagueName]);

  // Analytics: fire once each time the cheat-sheet view opens.
  useEffect(() => { if (viewMode === "cheatsheet") captureTierCheatSheet(); }, [viewMode]);

  // Clear pulse after animation plays
  useEffect(() => {
    if (!showPulse) return;
    const t = setTimeout(() => setShowPulse(false), 3500);
    return () => clearTimeout(t);
  }, [showPulse]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const hasAnyOverrides = Object.keys(userTierOverrides).length > 0 || Object.keys(tierOrder).length > 0 || Object.keys(tierNames).length > 0;
  const hasPosOverrides = Object.keys(userTierOverrides).some(k => k.startsWith(activePos + ":")) || Object.keys(tierOrder).some(k => k.startsWith(activePos + ":")) || Object.keys(tierNames).some(k => k.startsWith(activePos + ":"));
  const overrideKeySet = useMemo(() => new Set(Object.keys(userTierOverrides)), [userTierOverrides]);

  // Rename a tier label
  const handleRenameTier = useCallback((pos, tierNum, newName) => {
    const key = `${pos}:${tierNum}`;
    setTierNames(prev => {
      const next = { ...prev };
      if (newName === null) {
        delete next[key];
      } else {
        next[key] = newName;
      }
      return next;
    });
    triggerSaved();
  }, [triggerSaved]);

  // Build base tier assignments + apply moves (editor view)
  const { tierGroups, playersMap, allTiers } = useMemo(() => {
    if (viewMode !== "editor") return { tierGroups: {}, playersMap: {}, allTiers: [] };
    const { groups, pMap, tiers } = buildPosTierData(players, activePos, userTierOverrides, tierOrder);
    return { tierGroups: groups, playersMap: pMap, allTiers: tiers };
  }, [players, activePos, userTierOverrides, tierOrder, viewMode]);

  // Mobile tap-to-move handler
  const handleMoveToTier = useCallback((targetTier) => {
    if (!selectedPlayer) return;
    const overrideKey = `${selectedPlayer.pos}:${selectedPlayer.name}`;
    setUserTierOverrides(prev => ({ ...prev, [overrideKey]: targetTier }));
    // Clean up old tier order
    const oldOrderKey = `${selectedPlayer.pos}:${selectedPlayer.currentTier}`;
    setTierOrder(prev => {
      const next = { ...prev };
      if (next[oldOrderKey]) {
        next[oldOrderKey] = next[oldOrderKey].filter(n => n !== selectedPlayer.name);
        if (next[oldOrderKey].length === 0) delete next[oldOrderKey];
      }
      return next;
    });
    setSelectedPlayer(null);
    setShowPulse(false);
    triggerSaved();
    captureTierPlayerMoved({ method: "tap" });
  }, [selectedPlayer, triggerSaved]);

  // Mobile within-tier reorder handler
  const handleMobileReorder = useCallback((playerId, tierNum, direction) => {
    const tierIds = tierGroups[tierNum] || [];
    const oldIndex = tierIds.indexOf(playerId);
    if (oldIndex === -1) return;
    const newIndex = oldIndex + direction;
    if (newIndex < 0 || newIndex >= tierIds.length) return;
    const reordered = [...tierIds];
    reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, playerId);
    const orderKey = `${activePos}:${tierNum}`;
    setTierOrder(prev => ({ ...prev, [orderKey]: reordered }));
    triggerSaved();
  }, [activePos, tierGroups, triggerSaved]);

  const findTierForPlayer = useCallback((playerId) => {
    for (const [tier, ids] of Object.entries(tierGroups)) {
      if (ids.includes(playerId)) return Number(tier);
    }
    return null;
  }, [tierGroups]);

  // The tier the dragged player STARTED in (captured at drag start). Distinguishes
  // a genuine same-tier reorder (finalized by arrayMove in handleDragEnd) from a
  // cross-tier move (live-positioned by placeInTier in handleDragOver).
  const dragSourceTierRef = useRef(null);

  // Position-aware placement: put playerId into targetTier at the slot implied by
  // overId — spliced at the over-pill's index, or appended when over a lane. Also
  // scrubs the player from every other tier's custom order so nothing goes stale.
  // Idempotent: no-ops when membership + exact target order already match (DragOver
  // fires continuously, so this guard prevents setState churn / measure loops).
  const placeInTier = useCallback((playerId, targetTier, overId) => {
    const overrideKey = `${activePos}:${playerId}`;
    const targetOrderKey = `${activePos}:${targetTier}`;
    const rendered = tierGroups[targetTier] || [];
    const withoutDragged = rendered.filter(id => id !== playerId);
    const laneTier = parseLaneId(overId);
    let insertAt;
    if (laneTier !== null) {
      insertAt = withoutDragged.length; // dropped on the lane → append to end
    } else {
      const idx = withoutDragged.indexOf(overId);
      insertAt = idx === -1 ? withoutDragged.length : idx;
    }
    const desiredTarget = [...withoutDragged];
    desiredTarget.splice(insertAt, 0, playerId);

    // Idempotency guard: membership already targetTier, target order already exact,
    // and no other tier's order still lists this player.
    const sameOrder = rendered.length === desiredTarget.length && rendered.every((id, i) => id === desiredTarget[i]);
    const sameMembership = userTierOverrides[overrideKey] === targetTier;
    const stale = Object.keys(tierOrder).some(
      k => k !== targetOrderKey && k.startsWith(activePos + ":") && tierOrder[k]?.includes(playerId)
    );
    if (sameOrder && sameMembership && !stale) return;

    setUserTierOverrides(prev => (prev[overrideKey] === targetTier ? prev : { ...prev, [overrideKey]: targetTier }));
    setTierOrder(prev => {
      const next = { ...prev };
      next[targetOrderKey] = desiredTarget;
      Object.keys(next).forEach(k => {
        if (k === targetOrderKey || !k.startsWith(activePos + ":")) return;
        const filtered = next[k].filter(n => n !== playerId);
        if (filtered.length === 0) delete next[k];
        else next[k] = filtered;
      });
      return next;
    });
    triggerSaved();
  }, [activePos, tierGroups, userTierOverrides, tierOrder, triggerSaved]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    dragSourceTierRef.current = findTierForPlayer(event.active.id);
    setShowPulse(false);
  }, [findTierForPlayer]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveId(null);
    const origSource = dragSourceTierRef.current;
    dragSourceTierRef.current = null;
    if (!over || !active) return;

    const playerId = active.id;
    // Lane ids (`tier-<n>`) resolve directly; otherwise the tier of the over-pill.
    const laneTier = parseLaneId(over.id);
    const targetTier = laneTier !== null ? laneTier : findTierForPlayer(over.id);
    if (targetTier === null || targetTier === undefined) return;

    // Genuine same-tier reorder (player never left its start tier) → final position
    // pass via arrayMove. Cross-tier drags are already live-positioned by DragOver.
    if (origSource !== null && origSource === targetTier) {
      const tierIds = tierGroups[targetTier] || [];
      const oldIndex = tierIds.indexOf(playerId);
      const newIndex = laneTier !== null ? tierIds.length - 1 : tierIds.indexOf(over.id);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const reordered = arrayMove(tierIds, oldIndex, newIndex);
        setTierOrder(prev => ({ ...prev, [`${activePos}:${targetTier}`]: reordered }));
        triggerSaved();
        captureTierPlayerMoved({ method: "drag" });
      }
      return;
    }

    // Cross-tier: re-confirm the exact final slot (idempotent if DragOver already did it).
    placeInTier(playerId, targetTier, over.id);
    captureTierPlayerMoved({ method: "drag" });
  }, [activePos, findTierForPlayer, tierGroups, triggerSaved, placeInTier]);

  // NEVER capture analytics from here, or anywhere else mid-drag — posthog-on is a
  // necessary trigger for the #185 measure loop (docs/bugs/tier-drag-185.md).
  // tier_player_moved fires in handleDragEnd only.
  const handleDragOver = useCallback((event) => {
    const { active, over } = event;
    if (!over || !active) return;

    const playerId = active.id;
    const laneTier = parseLaneId(over.id);
    const targetTier = laneTier !== null ? laneTier : findTierForPlayer(over.id);
    if (targetTier === null || targetTier === undefined) return;

    // MEMBERSHIP ONLY: act solely when the player's CURRENT tier differs from the
    // target — the moment the drag crosses into a new tier. Comparing against the
    // drag-START tier re-ran placeInTier on every over event after crossing
    // (insert shifts pills → over-target flips → loop to max update depth).
    // Final position is settled in handleDragEnd.
    if (findTierForPlayer(playerId) === targetTier) return;
    placeInTier(playerId, targetTier, over.id);
  }, [findTierForPlayer, placeInTier]);

  const posColor = POS_COLORS[activePos] || { bg: "#334155", text: "#e2e8f0" };
  const draggedPlayer = activeId ? playersMap[activeId] : null;
  const isCheatSheet = viewMode === "cheatsheet";

  // Read-only badge sets. Memoized on the Set identities so pills don't
  // re-render on unrelated TierBuilder state (drag, tab switches).
  const badgeSets = useMemo(
    () => (watchlist || avoids ? { watchlist: watchlist || EMPTY_BADGES.watchlist, avoids: avoids || EMPTY_BADGES.avoids } : EMPTY_BADGES),
    [watchlist, avoids]
  );

  return (
    <TierBadgeContext.Provider value={badgeSets}>
    <div style={{ maxWidth: isCheatSheet ? 900 : 1100, margin: "0 auto", padding: "20px 28px" }}>
      <TierMobileStyles />

      {/* League context — tier edits are saved per league, so say which one.
          no-print: the printed cheat sheet doesn't want an instructional line. */}
      <LeagueContextLine className="tier-cheat-sheet-no-print" text="Build your tiers for" name={activeLeagueName}
        onSetup={onOpenSettings} setupSource="tier_builder" />

      {/* Tab Bar */}
      <div className="tier-tab-bar tier-cheat-sheet-no-print" style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        {/* Position tabs */}
        {POSITIONS.map(pos => {
          const pc = POS_COLORS[pos] || { bg: "#334155", text: "#e2e8f0" };
          const isActive = viewMode === "editor" && pos === activePos;
          return (
            <button
              key={pos}
              className="tier-pos-tab"
              onClick={() => { setActivePos(pos); setViewMode("editor"); }}
              style={{
                background: isActive ? pc.bg : "transparent",
                color: isActive ? pc.text : "#64748b",
                border: isActive ? "none" : "1px solid rgba(255,255,255,0.08)",
                borderRadius: 6,
                padding: "8px 20px",
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "inherit",
                cursor: "pointer",
                letterSpacing: "0.5px",
                transition: "all 0.15s ease",
              }}
            >
              {pos}
            </button>
          );
        })}

        {/* Divider */}
        <div className="tier-tab-divider" style={{ width: 1, height: 24, background: "rgba(255,255,255,0.1)", margin: "0 4px" }} />

        {/* Cheat Sheet + reset icon wrapper — stays together on wrap */}
        <div className="tier-cheatsheet-group" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            className="tier-cheatsheet-tab"
            onClick={() => setViewMode("cheatsheet")}
            style={{
              background: isCheatSheet ? "#94a3b8" : "transparent",
              color: isCheatSheet ? "#0f172a" : "#94a3b8",
              border: isCheatSheet ? "none" : "1px solid rgba(148,163,184,0.25)",
              borderRadius: 6,
              padding: "8px 16px",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: "pointer",
              letterSpacing: "0.5px",
              transition: "all 0.15s ease",
            }}
          >
            {"\uD83D\uDCCB"} CHEAT SHEET
          </button>

          {/* Print button (cheat sheet only) */}
          {isCheatSheet && (
            <button
              onClick={() => window.print()}
              style={{
                background: "transparent",
                color: "#94a3b8",
                border: "1px solid rgba(148,163,184,0.25)",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 11,
                fontWeight: 600,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              Print
            </button>
          )}

          {/* Reset icon + dropdown */}
          {!isCheatSheet && hasAnyOverrides && (
            <div style={{ position: "relative" }}>
              <button
                onClick={() => { setShowResetMenu(prev => !prev); setConfirmReset(null); }}
                style={{
                  width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                  background: "none", border: "none", color: "#94a3b8", fontSize: 16,
                  cursor: "pointer", borderRadius: 6, padding: 0, fontFamily: "inherit",
                  transition: "color 0.15s ease",
                }}
                onMouseEnter={e => e.currentTarget.style.color = "#e2e8f0"}
                onMouseLeave={e => e.currentTarget.style.color = "#94a3b8"}
                title="Reset tiers"
              >{"\u21BA"}</button>
              {showResetMenu && (
                <>
                  <div onClick={() => { setShowResetMenu(false); setConfirmReset(null); }} style={{ position: "fixed", inset: 0, zIndex: 99 }} />
                  <div style={{
                    position: "absolute", top: 32, right: 0, zIndex: 100,
                    background: "#1a2332", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8,
                    padding: "4px 0", minWidth: 140, boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                    animation: "fadeInUp 0.15s ease-out",
                  }}>
                    {hasPosOverrides && (
                      <div
                        onClick={() => {
                          if (confirmReset === "pos") {
                            const clearPos = (prev) => {
                              const next = { ...prev };
                              Object.keys(next).forEach(k => { if (k.startsWith(activePos + ":")) delete next[k]; });
                              return next;
                            };
                            setUserTierOverrides(clearPos);
                            setTierOrder(clearPos);
                            setTierNames(clearPos);
                            setShowResetMenu(false);
                            setConfirmReset(null);
                          } else {
                            setConfirmReset("pos");
                          }
                        }}
                        style={{
                          padding: "8px 14px", fontSize: 11, fontWeight: 600,
                          color: confirmReset === "pos" ? "#ff6b6b" : "#ef4444",
                          cursor: "pointer", whiteSpace: "nowrap", transition: "background 0.1s ease",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >{confirmReset === "pos" ? "Confirm?" : `Reset ${activePos}s`}</div>
                    )}
                    <div
                      onClick={() => {
                        if (confirmReset === "all") {
                          setUserTierOverrides({});
                          setTierOrder({});
                          setTierNames({});
                          setShowResetMenu(false);
                          setConfirmReset(null);
                        } else {
                          setConfirmReset("all");
                        }
                      }}
                      style={{
                        padding: "8px 14px", fontSize: 11, fontWeight: 600,
                        color: confirmReset === "all" ? "#ff6b6b" : "#ef4444",
                        cursor: "pointer", whiteSpace: "nowrap", transition: "background 0.1s ease",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >{confirmReset === "all" ? "Confirm?" : "Reset All"}</div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>


      {/* Auto-save note + toast */}
      {!isCheatSheet && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 10, color: hasEverChanged ? "#22c55e" : "#94a3b8", fontStyle: hasEverChanged ? "normal" : "italic", fontWeight: 500, transition: "color 0.3s ease" }}>
            {hasEverChanged ? "\u2713 Auto-saved" : "Changes save automatically"}
          </span>
          <span style={{
            fontSize: 11,
            color: "#22c55e",
            fontWeight: 600,
            opacity: showSaved ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}>
            {"\u2713"} Saved
          </span>
        </div>
      )}

      {/* Cheat Sheet View */}
      {isCheatSheet && (
        <CheatSheet
          // Remount on league switch: forces the marks (and the values
          // snapshot) to re-read under the new league's storage key.
          key={activeLeagueName || "DEFAULT"}
          players={players}
          settings={settings}
          userTierOverrides={userTierOverrides}
          overrideKeys={overrideKeySet}
          tierNames={tierNames}
          activeLeagueName={activeLeagueName}
        />
      )}

      {/* Stats-icon hint — same component and same
          auctionArchitect_statsHintSeen flag as the player lists, so
          dismissing it anywhere kills it everywhere. Sits above the lanes
          (desktop and mobile both) rather than inside the DndContext, which
          only covers desktop. Skipped on the cheat sheet, and no-print so it
          never reaches paper. */}
      {!isCheatSheet && (
        <div className="tier-cheat-sheet-no-print" style={{ marginBottom: 8 }}>
          <StatsHintStrip />
        </div>
      )}

      {/* Editor / Swim Lane View */}
      {!isCheatSheet && !isMobile && (
        <DndContext
          sensors={sensors}
          collisionDetection={collideHybrid}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          {/* First-time hint */}
          {!hasAnyOverrides && (
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10, textAlign: "center" }}>
              Drag players between tiers to customize your rankings
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {allTiers.map(tier => (
              <TierLane
                key={tier.tier}
                tier={tier}
                tierNum={tier.tier}
                playerIds={tierGroups[tier.tier] || []}
                playersMap={playersMap}
                posColor={posColor}
                activePos={activePos}
                overrideKeys={overrideKeySet}
                pulse={showPulse && (tier.tier === 1 || tier.tier === 2)}
                customName={tierNames[`${activePos}:${tier.tier}`]}
                onRename={(name) => handleRenameTier(activePos, tier.tier, name)}
              />
            ))}
          </div>

          <DragOverlay dropAnimation={null}>
            {draggedPlayer ? <PlayerPill player={draggedPlayer} posColor={posColor} ghost isOverridden /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Mobile: tap-to-move editor */}
      {!isCheatSheet && isMobile && (
        <>
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: selectedPlayer ? 4 : 10, textAlign: "center" }}>
            {selectedPlayer
              ? <span>Moving <span style={{ color: "#FF9500", fontWeight: 600 }}>{shortName(selectedPlayer.name)}</span> — tap a tier to move, then <span style={{ color: "#22c55e", fontWeight: 700 }}>✓ Done</span> to lock</span>
              : "Tap a player, then tap a tier to move them"}
          </div>
          {selectedPlayer && (
            <div
              onClick={() => setSelectedPlayer(null)}
              style={{ fontSize: 10, color: "#94a3b8", marginBottom: 10, textAlign: "center", cursor: "pointer", WebkitTapHighlightColor: "transparent" }}
            >
              Tap here to lock placement
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {allTiers.map(tier => (
              <MobileTierLane
                key={tier.tier}
                tier={tier}
                tierNum={tier.tier}
                playerIds={tierGroups[tier.tier] || []}
                playersMap={playersMap}
                posColor={posColor}
                activePos={activePos}
                overrideKeys={overrideKeySet}
                pulse={showPulse && (tier.tier === 1 || tier.tier === 2)}
                selectedPlayer={selectedPlayer}
                onSelectPlayer={setSelectedPlayer}
                onMoveToTier={handleMoveToTier}
                onReorder={handleMobileReorder}
                customName={tierNames[`${activePos}:${tier.tier}`]}
                onRename={(name) => handleRenameTier(activePos, tier.tier, name)}
              />
            ))}
          </div>

          <ScrollMoreIndicator />
        </>
      )}
    </div>
    </TierBadgeContext.Provider>
  );
});

export default TierBuilder;
